/**
 * Quality Check Agent -- Parse Functions Only
 * AI call logic da chuyen sang Workers API (handlers/qualityCheck.ts)
 * File nay chi giu lai parse CSV functions cho frontend dung
 */

import { AuditDetails } from '../../types';
import { splitSections, parseSection, parseNumber } from '../shared/csvParser';

/**
 * Parse CSV response into AuditDetails object (QC version)
 */
export const parseCSVQualityCheckResponse = (csvText: string, totalSegments: number): AuditDetails => {
    const sections = splitSections(csvText);

    // Parse SCORES section
    const scoresContent = sections.get('SCORES') || '';
    const scoresSection = parseSection(scoresContent);
    const scoresRow = scoresSection?.rows[0] || {};

    const overallScore = parseNumber(scoresRow['overall'] || '0');
    const componentScores = {
        accuracy: parseNumber(scoresRow['accuracy'] || '0'),
        terminology: parseNumber(scoresRow['terminology'] || '0'),
        tone: parseNumber(scoresRow['style'] || scoresRow['tone'] || '0'),
        fluency: parseNumber(scoresRow['fluency'] || '0'),
        completeness: parseNumber(scoresRow['completeness'] || '0'),
        style: parseNumber(scoresRow['style'] || '0')
    };

    // Initialize segments with default "Perfect" state
    const segmentMappings: any[] = [];
    for (let i = 1; i <= totalSegments; i++) {
        segmentMappings.push({
            sourceIndex: i,
            humanIndices: [],
            isMissing: false,
            score: 10,
            rules: '',
            errors: '',
            reviews: '',
            notes: '',
            issues: '',
            suggestions: ''
        });
    }

    // Detect format: new (has ISSUE_TYPES) vs old (has ISSUES or direct issues column)
    const hasIssueTypes = sections.has('ISSUE_TYPES');

    if (hasIssueTypes) {
        // ===== NEW FORMAT: ref-based =====
        const issueTypesSection = parseSection(sections.get('ISSUE_TYPES')!);
        const issueTypeMap = new Map<string, { category: string; severity: string; description: string }>();
        (issueTypesSection?.rows || []).forEach(row => {
            issueTypeMap.set(row['ref'] || '', {
                category: row['category'] || '',
                severity: row['severity'] || '',
                description: row['description'] || ''
            });
        });

        const segmentsRaw = sections.get('SEGMENTS') || '';
        const segLines = segmentsRaw.split('\n').map(l => l.trim()).filter(Boolean);

        const dataLines: string[] = [];
        for (const line of segLines) {
            if (/^source/i.test(line)) continue;
            if (/^\d+\|/.test(line)) {
                dataLines.push(line);
            } else {
                if (dataLines.length > 0) {
                    const cleaned = line.replace(/^(Gợi ý|Suggestion)\s*:\s*/i, '');
                    dataLines[dataLines.length - 1] += ' ' + cleaned;
                }
            }
        }

        for (const line of dataLines) {
            const parts = line.split('|');
            if (parts.length < 3) continue;

            const idx = parseNumber(parts[0]);
            const segment = segmentMappings.find(s => s.sourceIndex === idx);
            if (!segment) continue;

            segment.score = parseNumber(parts[1] || '10');
            const refStr = parts[2]?.trim() || '';
            if (parts.length >= 5) {
                segment.notes = parts[3]?.trim() || '';
                segment.suggestions = parts.slice(4).join('|').trim();
            } else {
                segment.suggestions = parts.slice(3).join('|').trim();
            }

            const refs = refStr.split(',').map(r => r.trim());
            const issueTexts: string[] = [];
            for (const ref of refs) {
                const issueType = issueTypeMap.get(ref);
                if (issueType) {
                    issueTexts.push(`[${issueType.severity}] ${issueType.category}: ${issueType.description}`);
                } else if (ref) {
                    issueTexts.push(ref);
                }
            }
            segment.issues = issueTexts.join('\n');
        }

        const evaluationSection = parseSection(sections.get('EVALUATION') || '');
        const evaluationMap: Record<string, string> = {};
        (evaluationSection?.rows || []).forEach(row => {
            if (row['criteria'] && row['assessment']) {
                evaluationMap[row['criteria'].toLowerCase()] = row['assessment'];
            }
        });

        const summarySection = parseSection(sections.get('SUMMARY') || '');
        const suggestedImprovement = (summarySection?.rows || [])
            .find(r => r['suggestedImprovement'])?.['suggestedImprovement'] || '';

        return {
            overallScore,
            componentScores,
            segmentMappings,
            criticalErrors: [],
            glossaryRules: [],
            accuracyAnalysis: evaluationMap['accuracy'] || '',
            fluencyAnalysis: evaluationMap['fluency'] || '',
            completenessAnalysis: evaluationMap['completeness'] || '',
            terminologyAnalysis: evaluationMap['terminology'] || '',
            toneAnalysis: evaluationMap['style'] || '',
            suggestedPromptUpdate: suggestedImprovement,
            isTruncated: !sections.has('SUMMARY') && !sections.has('EVALUATION')
        };
    } else {
        // ===== OLD FORMAT =====
        const segmentsSection = parseSection(sections.get('SEGMENTS') || '');
        if (segmentsSection?.rows) {
            segmentsSection.rows.forEach(row => {
                const idx = parseNumber(row['sourceIndex'] || '0');
                const segment = segmentMappings.find(s => s.sourceIndex === idx);
                if (segment) {
                    segment.score = parseNumber(row['score'] || '10');
                    segment.issues = row['issues'] || '';
                    segment.suggestions = row['suggestions'] || '';
                    segment.rules = row['rules'] || '';
                    segment.errors = row['errors'] || '';
                    segment.reviews = row['reviews'] || '';
                    segment.notes = row['notes'] || '';
                }
            });
        }

        const issuesSection = parseSection(sections.get('ISSUES') || '');
        if (issuesSection?.rows) {
            issuesSection.rows.forEach(issueRow => {
                const idx = parseNumber(issueRow['segmentIndex'] || issueRow['sourceIndex'] || '0');
                const segment = segmentMappings.find(s => s.sourceIndex === idx);
                if (segment) {
                    const issueText = `[${issueRow['severity']}] ${issueRow['category']}: ${issueRow['description']} -> ${issueRow['suggestion']}`;
                    segment.issues = segment.issues ? `${segment.issues}\n${issueText}` : issueText;
                }
            });
        }

        const errorsSection = parseSection(sections.get('CRITICAL_ERRORS') || '');
        const criticalErrors = (errorsSection?.rows || []).map(row => ({
            original: row['original'] || '',
            ai_choice: row['ai_choice'] || '',
            better_choice: row['better_choice'] || '',
            reason: row['reason'] || '',
            severity: (row['severity'] || 'Warning') as 'Critical Error' | 'Error' | 'Warning'
        }));

        const analysisSection = parseSection(sections.get('ANALYSIS') || '');
        const analysisMap: Record<string, string> = {};
        (analysisSection?.rows || []).forEach(row => {
            analysisMap[row['category'] || row['type'] || ''] = row['summary'] || row['content'] || '';
        });

        return {
            overallScore,
            componentScores,
            segmentMappings,
            criticalErrors,
            glossaryRules: [],
            accuracyAnalysis: analysisMap['accuracy'] || '',
            terminologyAnalysis: analysisMap['terminology'] || '',
            fluencyAnalysis: analysisMap['fluency'] || '',
            completenessAnalysis: analysisMap['completeness'] || '',
            toneAnalysis: analysisMap['style'] || analysisMap['tone'] || '',
            suggestedPromptUpdate: analysisMap['suggestedImprovement'] || analysisMap['suggestedPromptUpdate'] || '',
            isTruncated: !sections.has('ANALYSIS') && !sections.has('SUMMARY')
        };
    }
};
